import { useContext, useEffect } from 'react'

import { useMutation } from '@apollo/client'
import Button from 'Root/App/Button'
import Paragraph from 'Root/App/Paragraph'
import PreTitle from 'Root/App/PreTitle'
import Title from 'Root/App/Title'
import filter from 'lodash/filter'
import isNil from 'lodash/isNil'
import join from 'lodash/join'
import map from 'lodash/map'
import size from 'lodash/size'
import sum from 'lodash/sum'

import PageLinkWrapper from '../../PageLinkWrapper'
import { Context as StateContext } from '../../StateContext'
import MUTATION from '../../StateContext/app-quiz-state-context-storage.graphql'
import {
  getPointsForEssay,
  getPointsForMatching,
  getPointsForMissingWord,
  getPointsForMultipleChoice,
  getPointsForNumerical,
  getPointsForSingleChoice,
  getPointsForTrueFalse,
  getTotalPoints,
} from '../../utilities'
import Feedback from './Feedback'
import Recap from './Recap'
import MUTATION_ANSWER_QUIZ from './app-quiz-completion.graphql'

const Completion = ({ content }) => {
  const { state, dispatch } = useContext(StateContext)
  const [mutate, { called }] = useMutation(MUTATION)
  const [mutateQuiz, { called: calledQuiz }] = useMutation(MUTATION_ANSWER_QUIZ)

  const filtered = filter([
    content.label,
    `${!!content.basket ? content.basket : size(content.questions)} domande`,
  ])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pointsByAnswers = {}

  const points = map(state.questions, (question) => {
    let points = 0

    switch (question.__typename) {
      case 'QuizContentQuestionEssay':
        points = getPointsForEssay(question)
        break
      case 'QuizContentQuestionMatching':
        points = getPointsForMatching(
          state.content,
          question,
          state.answers?.[question.id]
        )
        break
      case 'QuizContentQuestionMissingWord':
        points = getPointsForMissingWord(question, state.answers?.[question.id])
        break
      case 'QuizContentQuestionMultipleChoice':
        points = getPointsForMultipleChoice(
          question,
          state.answers?.[question.id]
        )
        break
      case 'QuizContentQuestionNumerical':
        points = getPointsForNumerical(question, state.answers?.[question.id])
        break
      case 'QuizContentQuestionSingleChoice':
        points = getPointsForSingleChoice(
          question,
          state.answers?.[question.id]
        )
        break
      case 'QuizContentQuestionTrueFalse':
        points = getPointsForTrueFalse(question, state.answers?.[question.id])
        break
    }

    pointsByAnswers[question.id] = points

    return points
  })

  const completedWithSuccess = sum(points) >= getTotalPoints(state)

  // can be repeated only if
  // - the option was set to true
  // - the only failure suboption is set to true and quiz wasn't completed with failure
  // - the maximum is defined and current number of sessions + 1 is greater than current number
  const canRepeat =
    content.canRepeat &&
    (!content.canRepeatOnlyFailure ||
      (content.canRepeatOnlyFailure &&
        !(content.session?.success && completedWithSuccess))) &&
    (!Boolean(content.canRepeatMaximum) ||
      size(content?.mySessions || []) + 1 <= content.canRepeatMaximum)

  useEffect(() => {
    if (Boolean(state.content.session)) {
      return
    }

    if (Boolean(called)) {
      return
    }

    mutate({
      variables: {
        id: state.content.id,
        status: JSON.stringify({
          ...state,
          points: pointsByAnswers,
        }),
      },
    })
  }, [pointsByAnswers, called, mutate, state])

  useEffect(() => {
    if (Boolean(state.content.session)) {
      return
    }

    if (Boolean(calledQuiz)) {
      return
    }

    const handleMutateQuiz = async () => {
      const result = await mutateQuiz({
        variables: {
          id: state.content.id,
          input: JSON.stringify(state.answers),
        },
      })

      if (result.data?.answerQuiz?.mySessions) {
        dispatch({
          type: 'updateMySessions',
          value: result.data?.answerQuiz?.mySessions,
        })
      }
    }

    handleMutateQuiz()
  }, [pointsByAnswers, calledQuiz, mutateQuiz, state])

  const success = content.session?.success
  const evaluationWaiting =
    content.separateEvaluation && !content.session?.completed

  return (
    <div className="flex flex-col gap-3">
      <PageLinkWrapper slug={state.content.slug}>
        <PreTitle>{join(filtered, ' · ')}</PreTitle>
      </PageLinkWrapper>
      <PageLinkWrapper slug={state.content.slug}>
        <Title maxRows={3}>{content.title}</Title>
      </PageLinkWrapper>
      <Paragraph>{content.description}</Paragraph>

      <Feedback
        completed={content.session?.completed || false}
        content={state.content}
        success={!isNil(success) ? success : completedWithSuccess}
      />

      {Boolean(content.showSummary) && (
        <Recap answers={state.answers} questions={state.questions} />
      )}

      {canRepeat && !evaluationWaiting && (
        <Button size="L" onClick={() => dispatch({ type: 'repeat' })}>
          Ripeti
        </Button>
      )}
    </div>
  )
}

export default Completion
